import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  useTheme,
  Button,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Divider,
  Paper,
  Chip,
} from "@mui/material";
import { ArrowBack as ArrowBackIcon, Print as PrintIcon, PictureAsPdf as PictureAsPdfIcon } from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { generateStudentAccountDetailsPdf, printStudentAccountDetails } from "../../utils/studentAccountsDetailsPdf";
import { toast } from "react-hot-toast";
import { tokens } from "../../theme";
import useAccountantService from "../../api/services/AccountantService";
import useStudentService from "../../api/services/studentService";
import Header from "../../components/Header";

// Helper function to format currency
const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "0.00";
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return "0.00";
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numValue);
};

// Helper function to check if a value is non-zero
const hasValue = (value) => {
  if (value === null || value === undefined || value === "") return false;
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return false;
  return numValue > 0;
};

// Helper function to check if first term has any non-zero values
const hasFirstTermData = (data) => {
  if (!data) return false;
  const totalFees = parseFloat(data.firstTermFee || 0) +
    parseFloat(data.firstTermExamFee || 0) +
    parseFloat(data.firstTermOtherContribution || 0) +
    parseFloat(data.firstTermParentFee || 0);
  const debt = parseFloat(data.firstTermDebt || 0);
  const paidAmount = parseFloat(data.firstTermPaidAmount || 0);
  const totalAmountOwed = totalFees + debt;
  const totalDebt = totalAmountOwed - paidAmount;
  
  return totalFees > 0 || debt > 0 || paidAmount > 0 || totalDebt > 0;
};

// Helper function to check if second term has any non-zero values
const hasSecondTermData = (data) => {
  if (!data) return false;
  const totalFees = parseFloat(data.secondTermFee || 0) +
    parseFloat(data.secondTermExamFee || 0) +
    parseFloat(data.secondTermOtherContribution || 0) +
    parseFloat(data.secondTermParentFee || 0);
  const debt = parseFloat(data.secondTermDebt || 0);
  const paidAmount = parseFloat(data.secondTermPaidAmount || 0);
  const totalAmountOwed = totalFees + debt;
  const totalDebt = totalAmountOwed - paidAmount;
  
  return totalFees > 0 || debt > 0 || paidAmount > 0 || totalDebt > 0;
};

const ViewStudentAccountantDetails = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { accountantId } = useParams();
  const navigate = useNavigate();

  const { getAccountantData } = useAccountantService();
  const { loadStudentDetails, getParentsByStudentId } = useStudentService();

  const [accountantData, setAccountantData] = useState(null);
  const [studentData, setStudentData] = useState(null);
  const [parentsData, setParentsData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (accountantId) {
      loadAccountantData();
    }
  }, [accountantId]);

  const loadAccountantData = async () => {
    try {
      setLoading(true);
      const data = await getAccountantData(accountantId);
      setAccountantData(data);

      // Load student details if studentId is available
      if (data?.studentId) {
        try {
          const student = await loadStudentDetails(data.studentId);
          setStudentData(student);
          
          // Load parents details for this student
          try {
            const parents = await getParentsByStudentId(data.studentId);
            setParentsData(Array.isArray(parents) ? parents : []);
          } catch (error) {
            console.error("Error loading parents details:", error);
            setParentsData([]);
          }
        } catch (error) {
          console.error("Error loading student details:", error);
        }
      }
    } catch (error) {
      toast.error("Failed to load accountant details");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (accountantData?.studentId) {
      navigate(`/students-accounts-details/${accountantData.studentId}`);
    } else {
      navigate(-1);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="60vh"
      >
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  if (!accountantData) {
    return (
      <Box m="20px">
        <Box mb={2}>
          <Header
            title="ACCOUNT DETAILS"
            subtitle="View detailed financial account information"
          />
        </Box>
        <Paper
          elevation={3}
          sx={{
            p: 3,
            backgroundColor:
              theme.palette.mode === "dark"
                ? colors.primary[400]
                : "#ffffff",
            borderRadius: "8px",
            textAlign: "center",
          }}
        >
          <Typography variant="h6" color="error">
            Account details not found
          </Typography>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mt: 2 }}
          >
            Go Back
          </Button>
        </Paper>
      </Box>
    );
  }

  const InfoCard = ({ title, value, icon }) => (
    <Card
      sx={{
        height: "100%",
        backgroundColor:
          theme.palette.mode === "dark"
            ? colors.primary[400]
            : "#ffffff",
        border: `1px solid ${
          theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[300]
        }`,
        borderRadius: "8px",
        transition: "transform 0.2s, box-shadow 0.2s",
        boxShadow: theme.palette.mode === "dark" 
          ? "0 2px 8px rgba(0,0,0,0.2)" 
          : "0 2px 8px rgba(0,0,0,0.1)",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: theme.palette.mode === "dark" 
            ? "0 8px 16px rgba(0,0,0,0.3)" 
            : "0 8px 16px rgba(0,0,0,0.15)",
        },
      }}
    >
      <CardContent>
        <Typography
          variant="body2"
          color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[600]}
          sx={{ mb: 1, fontWeight: 500 }}
        >
          {title}
        </Typography>
        <Typography
          variant="h5"
          color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[500]}
          sx={{ fontWeight: 600 }}
        >
          {value}
        </Typography>
      </CardContent>
    </Card>
  );

  const handlePrint = () => {
    printStudentAccountDetails(accountantData, studentData);
  };

  const handleDownloadPdf = () => {
    generateStudentAccountDetailsPdf(accountantData, studentData);
  };

  return (
    <Box m="20px">
      <Box mb={2} display="flex" flexWrap="wrap" alignItems="center" justifyContent="space-between" gap={1}>
        <Header
          title="ACCOUNT DETAILS"
          subtitle="View detailed financial account information"
        />
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            size="small"
          >
            Print
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleDownloadPdf}
            size="small"
          >
            Download PDF
          </Button>
        </Box>
      </Box>

      {/* Student Information Section */}
      {studentData && (
        <Paper
          elevation={3}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor:
              theme.palette.mode === "dark"
                ? colors.primary[400]
                : "#ffffff",
            borderRadius: "8px",
            border: `1px solid ${
              theme.palette.mode === "dark" ? "transparent" : colors.grey[300]
            }`,
          }}
        >
          <Typography
            variant="h5"
            color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[500]}
            sx={{ mb: 2, fontWeight: 600 }}
          >
            Student Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography 
                variant="body2" 
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.primary[600]} 
                sx={{ mb: 0.5 }}
              >
                Student Name
              </Typography>
              <Typography 
                variant="body1" 
                color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[500]} 
                sx={{ fontWeight: 500 }}
              >
                {studentData.studentName || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography 
                variant="body2" 
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.primary[600]} 
                sx={{ mb: 0.5 }}
              >
                Class
              </Typography>
              <Typography 
                variant="body1" 
                color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[500]} 
                sx={{ fontWeight: 500 }}
              >
                {studentData.className || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography 
                variant="body2" 
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.primary[600]} 
                sx={{ mb: 0.5 }}
              >
                Student Number
              </Typography>
              <Typography 
                variant="body1" 
                color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[500]} 
                sx={{ fontWeight: 500 }}
              >
                {studentData.studentNumber || "N/A"}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Term Information */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 3,
          backgroundColor:
            theme.palette.mode === "dark"
              ? colors.primary[400]
              : "#ffffff",
          borderRadius: "8px",
          border: `1px solid ${
            theme.palette.mode === "dark" ? "transparent" : colors.grey[300]
          }`,
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography
            variant="h5"
            color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[500]}
            sx={{ fontWeight: 600 }}
          >
            Term Information
          </Typography>
          {accountantData?.termName && (
            <Chip
              label={accountantData.termName}
              color="secondary"
              sx={{ 
                fontWeight: 600,
                color: theme.palette.mode === "dark" ? "#fff" : "#000",
              }}
            />
          )}
        </Box>
      </Paper>

      {/* Financial Details Section */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 3,
          backgroundColor:
            theme.palette.mode === "dark"
              ? colors.primary[400]
              : "#ffffff",
          borderRadius: "8px",
          border: `1px solid ${
            theme.palette.mode === "dark" ? "transparent" : colors.grey[300]
          }`,
        }}
      >
        <Typography
          variant="h5"
          color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[500]}
          sx={{ mb: 3, fontWeight: 600 }}
        >
          Financial Details
        </Typography>

        <Grid container spacing={3}>
          {/* First Term Debt */}
          {accountantData && hasValue(accountantData.firstTermDebt) && (
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard
                title="First Term Debt"
                value={`${formatCurrency(accountantData.firstTermDebt)}`}
              />
            </Grid>
          )}

          {/* Second Term Debt */}
          {accountantData && hasValue(accountantData.secondTermDebt) && (
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard
                title="Second Term Debt"
                value={`${formatCurrency(accountantData.secondTermDebt)}`}
              />
            </Grid>
          )}

          {/* First Term Fees */}
          {accountantData && hasValue(accountantData.firstTermFee) && (
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard
                title="First Term Fee"
                value={`${formatCurrency(accountantData.firstTermFee)}`}
              />
            </Grid>
          )}

          {/* Second Term Fees */}
          {accountantData && hasValue(accountantData.secondTermFee) && (
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard
                title="Second Term Fee"
                value={`${formatCurrency(accountantData.secondTermFee)}`}
              />
            </Grid>
          )}

          {/* First Term Exam Fee */}
          {accountantData && hasValue(accountantData.firstTermExamFee) && (
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard
                title="First Term Exam Fee"
                value={`${formatCurrency(accountantData.firstTermExamFee)}`}
              />
            </Grid>
          )}

          {/* Second Term Exam Fee */}
          {accountantData && hasValue(accountantData.secondTermExamFee) && (
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard
                title="Second Term Exam Fee"
                value={`${formatCurrency(accountantData.secondTermExamFee)}`}
              />
            </Grid>
          )}

          {/* First Term Other Contribution */}
          {accountantData && hasValue(accountantData.firstTermOtherContribution) && (
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard
                title="First Term Other Contribution"
                value={`${formatCurrency(accountantData.firstTermOtherContribution)}`}
              />
            </Grid>
          )}

          {/* Second Term Other Contribution */}
          {accountantData && hasValue(accountantData.secondTermOtherContribution) && (
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard
                title="Second Term Other Contribution"
                value={`${formatCurrency(accountantData.secondTermOtherContribution)}`}
              />
            </Grid>
          )}

          {/* First Term Parent Fee */}
          {accountantData && hasValue(accountantData.firstTermParentFee) && (
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard
                title="First Term Parent Fee"
                value={`${formatCurrency(accountantData.firstTermParentFee)}`}
              />
            </Grid>
          )}

          {/* Second Term Parent Fee */}
          {accountantData && hasValue(accountantData.secondTermParentFee) && (
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard
                title="Second Term Parent Fee"
                value={`${formatCurrency(accountantData.secondTermParentFee)}`}
              />
            </Grid>
          )}

          {/* First Term Paid Amount */}
          {accountantData && hasValue(accountantData.firstTermPaidAmount) && (
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard
                title="First Term Paid Amount"
                value={`${formatCurrency(accountantData.firstTermPaidAmount)}`}
              />
            </Grid>
          )}

          {/* Second Term Paid Amount */}
          {accountantData && hasValue(accountantData.secondTermPaidAmount) && (
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard
                title="Second Term Paid Amount"
                value={`${formatCurrency(accountantData.secondTermPaidAmount)}`}
              />
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Parents Information Section */}
      {parentsData.length > 0 && (
        <Paper
          elevation={3}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor:
              theme.palette.mode === "dark"
                ? colors.primary[400]
                : "#ffffff",
            borderRadius: "8px",
            border: `1px solid ${
              theme.palette.mode === "dark" ? "transparent" : colors.grey[300]
            }`,
          }}
        >
          <Typography
            variant="h5"
            color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[500]}
            sx={{ mb: 3, fontWeight: 600 }}
          >
            Parents Information
          </Typography>
          <Grid container spacing={3}>
            {parentsData.map((parent, index) => (
              <Grid item xs={12} sm={6} md={4} key={parent.id || parent.studentParentId || index}>
                <Card
                  sx={{
                    height: "100%",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? colors.primary[500]
                        : "#ffffff",
                    border: `1px solid ${
                      theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[300]
                    }`,
                    borderRadius: "8px",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    boxShadow: theme.palette.mode === "dark" 
                      ? "0 2px 8px rgba(0,0,0,0.2)" 
                      : "0 2px 8px rgba(0,0,0,0.1)",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: theme.palette.mode === "dark" 
                        ? "0 8px 16px rgba(0,0,0,0.3)" 
                        : "0 8px 16px rgba(0,0,0,0.15)",
                    },
                  }}
                >
                  <CardContent>
                    <Typography
                      variant="h6"
                      color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[500]}
                      sx={{ mb: 2, fontWeight: 600 }}
                    >
                      Parent {index + 1}
                    </Typography>
                    <Divider sx={{ mb: 2, borderColor: theme.palette.mode === "dark" ? colors.grey[700] : colors.grey[300] }} />
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="body2"
                        color={theme.palette.mode === "dark" ? colors.grey[300] : colors.primary[600]}
                        sx={{ mb: 0.5, fontWeight: 500 }}
                      >
                        Parent Name
                      </Typography>
                      <Typography
                        variant="body1"
                        color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[500]}
                        sx={{ fontWeight: 500 }}
                      >
                        {parent.parentName || "N/A"}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="body2"
                        color={theme.palette.mode === "dark" ? colors.grey[300] : colors.primary[600]}
                        sx={{ mb: 0.5, fontWeight: 500 }}
                      >
                        Email
                      </Typography>
                      <Typography
                        variant="body1"
                        color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[500]}
                        sx={{ fontWeight: 500 }}
                      >
                        {parent.email || "N/A"}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="body2"
                        color={theme.palette.mode === "dark" ? colors.grey[300] : colors.primary[600]}
                        sx={{ mb: 0.5, fontWeight: 500 }}
                      >
                        Phone Number
                      </Typography>
                      <Typography
                        variant="body1"
                        color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[500]}
                        sx={{ fontWeight: 500 }}
                      >
                        {parent.phoneNumber || "N/A"}
                      </Typography>
                    </Box>
                    {parent.createdAt && (
                      <Box>
                        <Typography
                          variant="body2"
                          color={theme.palette.mode === "dark" ? colors.grey[300] : colors.primary[600]}
                          sx={{ mb: 0.5, fontWeight: 500 }}
                        >
                          Created At
                        </Typography>
                        <Typography
                          variant="body2"
                          color={theme.palette.mode === "dark" ? colors.grey[400] : colors.primary[500]}
                        >
                          {parent.createdAt.includes('T') 
                            ? new Date(parent.createdAt).toLocaleDateString() 
                            : parent.createdAt}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Summary Section */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          backgroundColor:
            theme.palette.mode === "dark"
              ? colors.blueAccent[800]
              : "#ffffff",
          borderRadius: "8px",
          border: `1px solid ${
            theme.palette.mode === "dark" ? "transparent" : colors.grey[300]
          }`,
        }}
      >
        <Typography
          variant="h5"
          color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[500]}
          sx={{ mb: 2, fontWeight: 600 }}
        >
          Summary
        </Typography>
        <Divider 
          sx={{ 
            mb: 2,
            borderColor: theme.palette.mode === "dark" 
              ? colors.grey[700] 
              : colors.grey[300],
          }} 
        />
        <Grid container spacing={2}>
          {/* First Term Section */}
          {accountantData && hasFirstTermData(accountantData) && (
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 2,
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? colors.blueAccent[800]
                    : colors.blueAccent[800],
                borderRadius: "4px",
                border: `1px solid ${
                  theme.palette.mode === "dark" ? "transparent" : colors.blueAccent[600]
                }`,
                mb: 2,
              }}
            >
              <Typography 
                variant="body2" 
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.blueAccent[400]} 
                sx={{ mb: 1 }}
              >
                Total First Term Fees
              </Typography>
              <Typography 
                variant="body1" 
                color={theme.palette.mode === "dark" ? colors.grey[100] : colors.blueAccent[300]} 
                sx={{ fontWeight: 500, mb: 1 }}
              >
                {formatCurrency(
                  (parseFloat(accountantData.firstTermFee || 0) +
                    parseFloat(accountantData.firstTermExamFee || 0) +
                    parseFloat(accountantData.firstTermOtherContribution || 0) +
                    parseFloat(accountantData.firstTermParentFee || 0)).toString()
                )}
              </Typography>
              <Divider sx={{ my: 1.5, borderColor: theme.palette.mode === "dark" ? colors.grey[700] : "rgba(255,255,255,0.2)" }} />
              <Typography 
                variant="body2" 
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.blueAccent[400]} 
                sx={{ mb: 1 }}
              >
                First Term Debt (from record)
              </Typography>
              <Typography 
                variant="body1" 
                color={theme.palette.mode === "dark" ? colors.grey[100] : colors.blueAccent[300]} 
                sx={{ fontWeight: 500, mb: 1 }}
              >
                {formatCurrency(accountantData.firstTermDebt || 0)}
              </Typography>
              <Divider sx={{ my: 1.5, borderColor: theme.palette.mode === "dark" ? colors.grey[700] : "rgba(255,255,255,0.2)" }} />
              <Typography 
                variant="body2" 
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.blueAccent[400]} 
                sx={{ mb: 1 }}
              >
                Total First Term Amount Owed
              </Typography>
              <Typography 
                variant="body1" 
                color={theme.palette.mode === "dark" ? colors.grey[100] : colors.blueAccent[300]} 
                sx={{ fontWeight: 500, mb: 2 }}
              >
                {formatCurrency(
                  ((parseFloat(accountantData.firstTermFee || 0) +
                    parseFloat(accountantData.firstTermExamFee || 0) +
                    parseFloat(accountantData.firstTermOtherContribution || 0) +
                    parseFloat(accountantData.firstTermParentFee || 0)) +
                    parseFloat(accountantData.firstTermDebt || 0)).toString()
                )}
              </Typography>
              <Divider sx={{ my: 1.5, borderColor: theme.palette.mode === "dark" ? colors.grey[700] : "rgba(255,255,255,0.2)" }} />
              <Typography 
                variant="body2" 
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.blueAccent[400]} 
                sx={{ mb: 1 }}
              >
                First Term Paid Amount
              </Typography>
              <Typography 
                variant="body1" 
                color={theme.palette.mode === "dark" ? colors.grey[100] : colors.blueAccent[300]} 
                sx={{ fontWeight: 500, mb: 2 }}
              >
                {formatCurrency(accountantData.firstTermPaidAmount || 0)}
              </Typography>
              <Divider sx={{ my: 1.5, borderColor: theme.palette.mode === "dark" ? colors.grey[700] : "rgba(255,255,255,0.2)" }} />
              <Typography 
                variant="body2" 
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.blueAccent[400]} 
                sx={{ mb: 1 }}
              >
                Total First Term Debt
              </Typography>
              <Typography 
                variant="h6" 
                color={theme.palette.mode === "dark" ? colors.redAccent[400] : colors.redAccent[500]} 
                sx={{ fontWeight: 600 }}
              >
                {formatCurrency(
                  (((parseFloat(accountantData.firstTermFee || 0) +
                    parseFloat(accountantData.firstTermExamFee || 0) +
                    parseFloat(accountantData.firstTermOtherContribution || 0) +
                    parseFloat(accountantData.firstTermParentFee || 0)) +
                    parseFloat(accountantData.firstTermDebt || 0)) -
                    parseFloat(accountantData.firstTermPaidAmount || 0)).toString()
                )}
              </Typography>
            </Box>
          </Grid>
          )}
          {/* Second Term Section */}
          {accountantData && hasSecondTermData(accountantData) && (
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 2,
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? colors.blueAccent[800]
                    : colors.blueAccent[800],
                borderRadius: "4px",
                border: `1px solid ${
                  theme.palette.mode === "dark" ? "transparent" : colors.blueAccent[600]
                }`,
                mb: 2,
              }}
            >
              <Typography 
                variant="body2" 
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.blueAccent[400]} 
                sx={{ mb: 1 }}
              >
                Total Second Term Fees
              </Typography>
              <Typography 
                variant="body1" 
                color={theme.palette.mode === "dark" ? colors.grey[100] : colors.blueAccent[300]} 
                sx={{ fontWeight: 500, mb: 1 }}
              >
                {formatCurrency(
                  (parseFloat(accountantData.secondTermFee || 0) +
                    parseFloat(accountantData.secondTermExamFee || 0) +
                    parseFloat(accountantData.secondTermOtherContribution || 0) +
                    parseFloat(accountantData.secondTermParentFee || 0)).toString()
                )}
              </Typography>
              <Divider sx={{ my: 1.5, borderColor: theme.palette.mode === "dark" ? colors.grey[700] : "rgba(255,255,255,0.2)" }} />
              <Typography 
                variant="body2" 
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.blueAccent[400]} 
                sx={{ mb: 1 }}
              >
                Second Term Debt (from record)
              </Typography>
              <Typography 
                variant="body1" 
                color={theme.palette.mode === "dark" ? colors.grey[100] : colors.blueAccent[300]} 
                sx={{ fontWeight: 500, mb: 1 }}
              >
                {formatCurrency(accountantData.secondTermDebt || 0)}
              </Typography>
              <Divider sx={{ my: 1.5, borderColor: theme.palette.mode === "dark" ? colors.grey[700] : "rgba(255,255,255,0.2)" }} />
              <Typography 
                variant="body2" 
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.blueAccent[400]} 
                sx={{ mb: 1 }}
              >
                Total Second Term Amount Owed
              </Typography>
              <Typography 
                variant="body1" 
                color={theme.palette.mode === "dark" ? colors.grey[100] : colors.blueAccent[300]} 
                sx={{ fontWeight: 500, mb: 2 }}
              >
                {formatCurrency(
                  ((parseFloat(accountantData.secondTermFee || 0) +
                    parseFloat(accountantData.secondTermExamFee || 0) +
                    parseFloat(accountantData.secondTermOtherContribution || 0) +
                    parseFloat(accountantData.secondTermParentFee || 0)) +
                    parseFloat(accountantData.secondTermDebt || 0)).toString()
                )}
              </Typography>
              <Divider sx={{ my: 1.5, borderColor: theme.palette.mode === "dark" ? colors.grey[700] : "rgba(255,255,255,0.2)" }} />
              <Typography 
                variant="body2" 
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.blueAccent[400]} 
                sx={{ mb: 1 }}
              >
                Second Term Paid Amount
              </Typography>
              <Typography 
                variant="body1" 
                color={theme.palette.mode === "dark" ? colors.grey[100] : colors.blueAccent[300]} 
                sx={{ fontWeight: 500, mb: 2 }}
              >
                {formatCurrency(accountantData.secondTermPaidAmount || 0)}
              </Typography>
              <Divider sx={{ my: 1.5, borderColor: theme.palette.mode === "dark" ? colors.grey[700] : "rgba(255,255,255,0.2)" }} />
              <Typography 
                variant="body2" 
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.blueAccent[400]} 
                sx={{ mb: 1 }}
              >
                Total Second Term Debt
              </Typography>
              <Typography 
                variant="h6" 
                color={theme.palette.mode === "dark" ? colors.redAccent[400] : colors.redAccent[500]} 
                sx={{ fontWeight: 600 }}
              >
                {formatCurrency(
                  (((parseFloat(accountantData.secondTermFee || 0) +
                    parseFloat(accountantData.secondTermExamFee || 0) +
                    parseFloat(accountantData.secondTermOtherContribution || 0) +
                    parseFloat(accountantData.secondTermParentFee || 0)) +
                    parseFloat(accountantData.secondTermDebt || 0)) -
                    parseFloat(accountantData.secondTermPaidAmount || 0)).toString()
                )}
              </Typography>
            </Box>
          </Grid>
          )}
        </Grid>
      </Paper>

      {/* Payment instructions */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 3,
          backgroundColor:
            theme.palette.mode === "dark"
              ? colors.greenAccent[800]
              : colors.greenAccent[50],
          borderRadius: "8px",
          border: `1px solid ${
            theme.palette.mode === "dark" ? "transparent" : colors.greenAccent[200]
          }`,
        }}
      >
        <Typography
          variant="h5"
          color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[500]}
          sx={{ mb: 2, fontWeight: 600 }}
        >
          Malipo yafanyike kwenye  Account namba zifuatazo:-
        </Typography>
        <Divider
          sx={{
            mb: 2,
            borderColor: theme.palette.mode === "dark"
              ? colors.grey[700]
              : colors.greenAccent[300],
          }}
        />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body1"
                color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[600]}
                sx={{ fontWeight: 600, mb: 1 }}
              >
                CRDB BANK
              </Typography>
              <Typography
                variant="body2"
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.primary[500]}
                sx={{ fontSize: "1.1rem" }}
              >
                0152253103800
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body1"
                color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[600]}
                sx={{ fontWeight: 600, mb: 1 }}
              >
                NMB BANK
              </Typography>
              <Typography
                variant="body2"
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.primary[500]}
                sx={{ fontSize: "1.1rem" }}
              >
                21210019782
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body1"
                color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[600]}
                sx={{ fontWeight: 600, mb: 1 }}
              >
                MKOMBOZI BANK
              </Typography>
              <Typography
                variant="body2"
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.primary[500]}
                sx={{ fontSize: "1.1rem" }}
              >
                00120600737701
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 2, borderColor: theme.palette.mode === "dark" ? colors.grey[700] : colors.greenAccent[300] }} />
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body1"
                color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[600]}
                sx={{ fontWeight: 600, mb: 1 }}
              >
                ACCOUNT NAME
              </Typography>
              <Typography
                variant="body2"
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.primary[500]}
                sx={{ fontSize: "1.1rem" }}
              >
                ST. MARY&apos;S JUNIOR SEMINARY – VISIGA
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="body1"
                color={theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[600]}
                sx={{ fontWeight: 600, mb: 1 }}
              >
                Namba ya simu ya Seminari
              </Typography>
              <Typography
                variant="body2"
                color={theme.palette.mode === "dark" ? colors.grey[300] : colors.primary[500]}
                sx={{ fontSize: "1.1rem" }}
              >
                +255 767 927 537 (St. Mary&apos;s Seminary)
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default ViewStudentAccountantDetails;

